import { createClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Validate required environment variables
if (!supabaseUrl) {
  console.error('Missing SUPABASE_URL environment variable');
  console.log('Please add SUPABASE_URL to your .env file');
}

if (!supabaseAnonKey) {
  console.error('Missing SUPABASE_ANON_KEY environment variable');
  console.log('Please add SUPABASE_ANON_KEY to your .env file');
}

// Create Supabase client
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  }
);

// Database helper functions
export const db = {
  // Users
  async getUsers() {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  async getUserById(id) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .single();
    
    return { data, error };
  },

  async createUser(userData) {
    const { data, error } = await supabase
      .from('users')
      .insert([userData])
      .select()
      .single();
    
    return { data, error };
  },

  async updateUser(id, updates) {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    return { data, error };
  },

  async deleteUser(id) {
    const { data, error } = await supabase
      .from('users')
      .delete()
      .eq('id', id);
    
    return { data, error };
  },

  // Forms
  async getForms() {
    const { data, error } = await supabase
      .from('forms')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  async getFormById(id) {
    const { data, error } = await supabase
      .from('forms')
      .select('*')
      .eq('id', id)
      .single();
    
    return { data, error };
  },

  async getFormByType(formType) {
    const { data, error } = await supabase
      .from('forms')
      .select('*')
      .eq('form_type', formType)
      .eq('is_active', true)
      .single();
    
    return { data, error };
  },

  async createForm(formData) {
    const { data, error } = await supabase
      .from('forms')
      .insert([formData])
      .select()
      .single();
    
    return { data, error };
  },

  async updateForm(id, updates) {
    const { data, error } = await supabase
      .from('forms')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    return { data, error };
  },

  // Form Submissions
  async getSubmissions(filters = {}) {
    let query = supabase
      .from('form_submissions')
      .select(`
        *,
        forms(name, form_type),
        users(name, email)
      `)
      .order('submitted_at', { ascending: false });

    // Apply filters
    if (filters.formId) {
      query = query.eq('form_id', filters.formId);
    }
    if (filters.userId) {
      query = query.eq('user_id', filters.userId);
    }
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.dateFrom) {
      query = query.gte('submitted_at', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte('submitted_at', filters.dateTo);
    }

    const { data, error } = await query;
    return { data, error };
  },

  async getSubmissionById(id) {
    const { data, error } = await supabase
      .from('form_submissions')
      .select(`
        *,
        forms(name, form_type, fields),
        users(name, email, phone)
      `)
      .eq('id', id)
      .single();
    
    return { data, error };
  },

  async createSubmission(submissionData) {
    const { data, error } = await supabase
      .from('form_submissions')
      .insert([submissionData])
      .select()
      .single();
    
    return { data, error };
  },

  async updateSubmission(id, updates) {
    const { data, error } = await supabase
      .from('form_submissions')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    return { data, error };
  },

  // Announcements
  async getAnnouncements(published = true) {
    let query = supabase
      .from('announcements')
      .select(`
        *,
        users(name)
      `)
      .order('created_at', { ascending: false });

    if (published) {
      query = query.eq('is_published', true);
    }

    const { data, error } = await query;
    return { data, error };
  },

  async getAnnouncementById(id) {
    const { data, error } = await supabase
      .from('announcements')
      .select(`
        *,
        users(name)
      `)
      .eq('id', id)
      .single();
    
    return { data, error };
  },

  async createAnnouncement(announcementData) {
    const { data, error } = await supabase
      .from('announcements')
      .insert([announcementData])
      .select()
      .single();
    
    return { data, error };
  },

  async updateAnnouncement(id, updates) {
    const { data, error } = await supabase
      .from('announcements')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    return { data, error };
  },

  async deleteAnnouncement(id) {
    const { data, error } = await supabase
      .from('announcements')
      .delete()
      .eq('id', id);
    
    return { data, error };
  },

  // Staff Profiles
  async getStaffProfiles() {
    const { data, error } = await supabase
      .from('staff_profiles')
      .select(`
        *,
        users(name, email)
      `)
      .eq('is_visible', true)
      .order('display_order', { ascending: true });
    
    return { data, error };
  },

  async getStaffProfileById(id) {
    const { data, error } = await supabase
      .from('staff_profiles')
      .select(`
        *,
        users(name, email, phone)
      `)
      .eq('id', id)
      .single();
    
    return { data, error };
  },

  async createStaffProfile(profileData) {
    const { data, error } = await supabase
      .from('staff_profiles')
      .insert([profileData])
      .select()
      .single();
    
    return { data, error };
  },

  async updateStaffProfile(id, updates) {
    const { data, error } = await supabase
      .from('staff_profiles')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    return { data, error };
  },

  // Settings
  async getSettings() {
    const { data, error } = await supabase
      .from('site_settings')
      .select('*');
    
    return { data, error };
  },

  async getSetting(key) {
    const { data, error } = await supabase
      .from('site_settings')
      .select('value')
      .eq('key', key)
      .single();
    
    return { data: data?.value, error };
  },

  async updateSetting(key, value, description = '') {
    const { data, error } = await supabase
      .from('site_settings')
      .upsert([
        {
          key,
          value,
          description,
          updated_at: new Date().toISOString()
        }
      ])
      .select()
      .single();
    
    return { data, error };
  },

  // File Storage
  async uploadFile(bucket, path, file) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file);
    
    return { data, error };
  },

  async getFileUrl(bucket, path) {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);
    
    return data.publicUrl;
  },

  async deleteFile(bucket, path) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .remove([path]);
    
    return { data, error };
  }
};

// Connection test function
export const testConnection = async () => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('count')
      .limit(1);
    
    if (error) {
      console.error('Database connection test failed:', error);
      return false;
    }
    
    console.log('Database connection successful');
    return true;
  } catch (error) {
    console.error('Database connection error:', error);
    return false;
  }
};

// Initialize connection test on import
testConnection();

export default supabase;